import BaseScene from '@Scenes/BaseScene';

/*--------------- Config -------------------*/
import Config from '@Config';
import * as Scenes from '@Config/scenes';
import { LeaderboardIDs } from '@Config/leaderboards';
import AnalyticsEvents from '@Config/analytics_events';
import * as Networks from '@Config/networks';

/*--------------- Core -------------------*/
import Network from '@Networks/Localhost';
import Game from '@Game';

/*---------------- Utils --------------------*/
import Delay from '@Utils/Time/Delay';
import ToDataURL from '@Utils/Image/ToDataURL';

/**
 * Menu scene
 * @author charlie.bodman@kanoapps.com
 */
class StartScreenScene extends BaseScene
{
    /*------------- Camera --------------------*/
    private camera: Phaser.Cameras.Scene2D.Camera;

    /*------------- Logo --------------------*/
    private logo: Phaser.GameObjects.Image;
    private logoTweenRight: Phaser.Tweens.Tween;
    private logoTweenUpLeft: Phaser.Tweens.Tween;
    private logoTweenDownRight: Phaser.Tweens.Tween;

    /*------------- Background --------------------*/
    private background: Phaser.GameObjects.Image;
    private backgroundScaleTween: Phaser.Tweens.Tween;
    private backgroundTweenUp: Phaser.Tweens.Tween;
    private backgroundTweenDown: Phaser.Tweens.Tween;

    /*------------- Buttons -------------------*/
    private leaderboardButton: Phaser.GameObjects.Container;
    private startButtons: Phaser.GameObjects.Container;
    private startButtonsTween: Phaser.Tweens.Tween;

    /*------------- Back to Main --------------------*/
    private backButton: Phaser.GameObjects.Container;

    /*----------------- Text ------------------------*/
    private matchMakingText: Phaser.GameObjects.Text;

    /**
     * Contstructor
     */
    constructor()
    {
        super(Scenes.START_SCREEN);
    }

    /**
     * Create
     */
    public async create()
    {
        this.camera = this.sys.cameras.main.setBackgroundColor(0xFFFFFF);

        /**
         * Add game elements
         */
        this.addBackground();
        this.addLogo();
        this.addVersion();

        /**
         * Leaderboard support
         */
        if (Network.getSupportedAPIs().indexOf('getLeaderboardAsync') !== -1)
        {
            this.addLeaderboardButton();
        }

        this.addBackButton();
        this.addStartButtons();
        this.addMatchMakingText();

    }

    /**
     * Add match making text
     */
    private addMatchMakingText()
    {
        const gameSize = this.getGameSize();
        const matchMakingText = this.add.text(gameSize.width / 2, gameSize.height - 50, 'Finding match...');
        matchMakingText.setFontFamily('Permanent Marker');
        matchMakingText.setFontStyle('bold');
        matchMakingText.setColor('#333333');
        matchMakingText.setFontSize(30)
        matchMakingText.setAlign('right');
        matchMakingText.setOrigin(0.5);
        matchMakingText.setVisible(false);
        this.matchMakingText = matchMakingText;
    }

    /**
     * Adds start buttons
     */
    private addStartButtons()
    {
        const gameSize = this.getGameSize();

        const playSoloButton = this.make.image({ x: 0, y: 0, key: 'play_solo_button', add: false });
        playSoloButton.setScale(0.5, 0.5);
        playSoloButton.x = (gameSize.width / 2) - (playSoloButton.displayWidth / 2);
        playSoloButton.setInteractive({ cursor: 'pointer' });
        playSoloButton.on('pointerover', () => { playSoloButton.setAlpha(1) });
        playSoloButton.on('pointerout', () => { playSoloButton.setAlpha(0.9) });
        playSoloButton.on('pointerdown', this.playSolo);

        const playWithFriendsButton = this.make.image({ x: 0, y: 0, key: 'play_with_friends_button', add: false });
        playWithFriendsButton.setScale(0.5, 0.5);
        playWithFriendsButton.x = 230;
        playWithFriendsButton.setInteractive({ cursor: 'pointer' });
        playWithFriendsButton.on('pointerover', () => { playWithFriendsButton.setAlpha(1) });
        playWithFriendsButton.on('pointerout', () => { playWithFriendsButton.setAlpha(0.9) });
        playWithFriendsButton.on('pointerdown', this.playWithFriends);

        const challengeRandom = this.make.image({ x: 0, y: 0, key: 'challenge_random_button', add: false });
        challengeRandom.setScale(0.5, 0.5);
        challengeRandom.x = 500;
        challengeRandom.setInteractive({ cursor: 'pointer' });
        challengeRandom.on('pointerover', () => { challengeRandom.setAlpha(1) });
        challengeRandom.on('pointerout', () => { challengeRandom.setAlpha(0.9) });
        challengeRandom.on('pointerdown', this.challengeRandom);

        const startButtons = this.add.container(0, 0);

        if (Config.NETWORK === Networks.FACEBOOK)
        {
            startButtons.add(playWithFriendsButton);
            startButtons.add(challengeRandom);
        }
        else
        {
            startButtons.add(playSoloButton);
        }

        startButtons.setAlpha(0);
        startButtons.setY(360);

        this.startButtons = startButtons;

        this.startButtonsTween = this.add.tween({
            targets: [startButtons],
            alpha: 0.9,
            duration: 250,
            delay: 500
        });
    }


    /**
     * Adds version text
     */
    private addVersion()
    {
        const gameSize = this.getGameSize();
        const tapToStart = this.add.text(gameSize.width - 10, gameSize.height - 10, `Version: ${process.env.FRJ_VERSION}`);
        tapToStart.setFontFamily('Permanent Marker');
        tapToStart.setFontStyle('bold');
        tapToStart.setColor('#333333');
        tapToStart.setFontSize(12)
        tapToStart.setAlign('right');
        tapToStart.setOrigin(1);

    }
    /**
      * Switches context, utlizing network context
      */
    private async switchContext()
    {
        try
        {
            await Network.context.chooseAsync();

            const contextualLeaderboard = Game.leaderboards[LeaderboardIDs.CONTEXTUAL];

            await contextualLeaderboard.initializeAsync();

            /**
             * Check if the context is new.
             * If it's new, tell the other players about the game.
             */
            const leaderboardEntriesCount = await contextualLeaderboard.getEntryCountAsync();

            if (leaderboardEntriesCount <= 0)
            {
                const shareSplashTexture = this.textures.get('splash');

                const shareSplashImage = shareSplashTexture.getSourceImage() as HTMLImageElement;

                try
                {
                    await Network.updateAsync({
                        action: 'CUSTOM',
                        cta: "Play",
                        template: 'challenge',
                        image: ToDataURL(shareSplashImage),
                        strategy: 'IMMEDIATE',
                        text: `${Network.player.getName()} is challenging you to beat their score! `
                    });
                }
                catch (error)
                {
                    Network.logEvent(AnalyticsEvents.FAILED_TO_SEND_UPDATE_MESSAGE, null, { error_code: error.code, non_interaction: 'true' });
                }
            }
        }
        catch (error)
        {
            if (!error.code || error.code !== 'USER_INPUT')
            {
                Network.logEvent(AnalyticsEvents.FAILED_TO_SWITCH_CONTEXT, null, { error_code: error.code, non_interaction: 'true' });
            }
        }
    }

    /**
     * Adds leaderboard button
     */
    private addLeaderboardButton()
    {
        const gameSize = this.getGameSize();
        const leaderboardButton = this.add.container(gameSize.width - 80, gameSize.height - 80);

        const button = this.add.image(0, 0, 'btn_grey');
        button.setDisplaySize(100, 100);
        button.setInteractive();
        button.on('pointerdown', () =>
        {
            button.setDisplaySize(110, 110)
        });

        button.on('pointerup', () =>
        {
            button.setDisplaySize(100, 100);
            this.showLeaderboards();
        });

        const leaderboardIcon = this.add.image(0, 0, 'leaderboard_icon');
        leaderboardIcon.setDisplaySize(50, 50);

        leaderboardButton.add([button, leaderboardIcon]);
        this.leaderboardButton = leaderboardButton;
    }

    /**
     * Adds leaderboard button
     */
    private addBackButton()
    {
        const gameSize = this.getGameSize();
        const backButton = this.add.container(gameSize.width - 80, gameSize.height - 80);

        const button = this.add.image(0, 0, 'btn_grey');
        button.setDisplaySize(100, 100);
        button.setInteractive();
        button.on('pointerdown', () =>
        {
            button.setDisplaySize(110, 110)
        });

        button.on('pointerup', () =>
        {
            button.setDisplaySize(100, 100);
            this.goBack();
        });

        const backIcon = this.add.image(0, 0, 'btn_back');
        backIcon.setDisplaySize(50, 50);

        backButton.add([button, backIcon]);
        backButton.setVisible(false);

        this.backButton = backButton;
    }

    /**
     * Shows the leaderboards scene
     */
    private async showLeaderboards()
    {
        this.leaderboardButton.setVisible(false);

        this.backgroundScaleTween.pause();
        this.backgroundTweenDown.pause();
        this.backgroundTweenUp.play(true);

        this.logoTweenRight.pause();
        this.logoTweenDownRight.pause();
        this.logoTweenUpLeft.play(true);

        this.startButtons.setVisible(false);

        await Delay(1100);

        this.scene.launch(Scenes.LEADERBOARDS);

        this.backButton.setVisible(true);
    }

    /**
     * Go back to main scene
     */
    private async goBack()
    {
        this.scene.stop(Scenes.LEADERBOARDS);

        this.backgroundTweenUp.pause();
        this.backgroundTweenDown.play(true);

        this.logoTweenUpLeft.pause();
        this.logoTweenDownRight.play(true);

        this.backButton.setVisible(false);

        this.startButtonsTween.restart();
        this.startButtons.setVisible(true);

        await Delay(1100);

        this.leaderboardButton.setVisible(true);

        this.backgroundScaleTween.play(false);
    }

    /**
     * Add Background
     */
    private addBackground()
    {
        const gameSize = this.getGameSize();

        const backgroundX = gameSize.width / 2;
        const backgroundY = gameSize.height / 2;

        const background = this.add.image(backgroundX, backgroundY, 'background');
        background.setScale(1.1, 1.1);
        background.setOrigin(0.5);

        /**
         * Scale/Zoom Tween
         */
        this.backgroundScaleTween = this.tweens.add({
            targets: background,
            scaleX: 1.3,
            scaleY: 1.3,
            duration: 8000,
            ease: "Sine.easeInOut",
            yoyo: true,
            loop: Infinity
        })

        /**
         * Slide up tween
         */
        this.backgroundTweenUp = this.tweens.add({
            targets: background,
            y: -150,
            duration: 1000,
            ease: "Sine.easeInOut"
        });

        this.backgroundTweenUp.pause();

        /**
         * Slide down tween
         */
        this.backgroundTweenDown = this.add.tween({
            targets: background,
            y: backgroundY,
            duration: 1000,
            ease: "Sine.easeInOut",
        });

        this.backgroundTweenDown.pause();

        this.background = background;
    }

    /**
     * Adds logo image to the scene
     */
    private addLogo()
    {
        const gameSize = this.getGameSize();

        const logoX = gameSize.centerX - 400;
        const logoY = gameSize.centerY - 200;

        const logo = this.add.image(-500, logoY, 'logo');
        logo.setOrigin(0, 0);

        /**
         * Tween Right
         */
        this.logoTweenRight = this.tweens.add({
            targets: logo,
            x: logoX,
            duration: 1500,
            ease: "Elastic",
            easeParams: [1, 0.7],
            delay: 0
        });

        /**
         * Tween Up Left
         */
        this.logoTweenUpLeft = this.add.tween({
            targets: logo,
            y: 20,
            x: 20,
            scaleX: 0.3,
            scaleY: 0.3,
            duration: 1000,
            ease: "Sine.easeInOut",
        });

        this.logoTweenUpLeft.pause();

        /**
         * Tween Down Right
         */
        this.logoTweenDownRight = this.add.tween({
            targets: logo,
            x: logoX,
            y: logoY,
            scaleX: 1,
            scaleY: 1,
            ease: "Sine.easeInOut",
        });

        this.logoTweenDownRight.pause();


        this.logo = logo;
    }

    /**
     * Plays solo
     */
    private playSolo = () =>
    {
        this.startSceneTransition();
    }

    /**
     * Starts scene with context switching
     */
    private playWithFriends = async () =>
    {
        Network.logEvent(AnalyticsEvents.PLAY_WITH_FRIENDS);

        if (Network.getSupportedAPIs().indexOf('context.chooseAsync') !== -1)
        {
            await this.switchContext();

            this.startSceneTransition();
        }
        else
        {
            this.scene.launch(Scenes.FEATURE_NOT_AVAILABLE);
        }
    }

    /**
     * Starts scene with challenge random
     */
    private challengeRandom = async () =>
    {
        Network.logEvent(AnalyticsEvents.CHALLENGE_RANDOM);

        if (Network.getSupportedAPIs().indexOf('matchPlayerAsync') !== -1)
        {
            this.startButtons.setVisible(false);

            if (this.leaderboardButton)
            {
                this.leaderboardButton.setVisible(false);
            }

            this.matchMakingText.setText('Finding match...');

            this.matchMakingText.setVisible(true);

            try
            {
                const canMatchPlayer = await Network.checkCanPlayerMatchAsync();

                if (canMatchPlayer)
                {
                    await Network.matchPlayerAsync('random', true);

                    const contextualLeaderboard = Game.leaderboards[LeaderboardIDs.CONTEXTUAL];

                    await contextualLeaderboard.initializeAsync();

                    this.startSceneTransition();
                }
                else
                {
                    throw new Error('Not eligible to match');
                }
            }
            catch (error)
            {
                this.startButtonsTween.restart();

                this.startButtons.setVisible(true);

                this.matchMakingText.setText('Could not find match.');

                if (this.leaderboardButton)
                {
                    this.leaderboardButton.setVisible(true);
                }

                Network.logEvent(AnalyticsEvents.FAILED_TO_CHALLENGE_RANDOM, null, { error_code: error.code, non_interaction: 'true' });
            }
        }
        else
        {
            this.scene.launch(Scenes.FEATURE_NOT_AVAILABLE);
        }
    }

    /**
     * Starts the scene transaction
     */
    private startSceneTransition = async () =>
    {
        this.backgroundScaleTween.stop();
        this.input.enabled = false;

        this.tweens.add({
            targets: [this.matchMakingText, this.startButtons, this.logo, this.background, this.leaderboardButton],
            alpha: 0,
            duration: 1000,
            onComplete: this.startNextScene
        });

        this.camera.flash(500);
        this.camera.fadeOut(1000, 255, 255, 255);
    }

    /**
     * Starts the next scene
     */
    private startNextScene = async () =>
    {
        this.scene.start(Scenes.CONTROLS_TUTORIAL)
    }
}

export default StartScreenScene;